class CollectionInfiniteScroll extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', this.onClick.bind(this));
    this.container = document.getElementById('ProductGrid');
    this.isLoading = false;
    this.autoLoadObserver = null;
  }

  // 2026-08-05: auto-load on scroll.
  // "Enable infinite scroll" only ever rendered a LOAD MORE button and waited for a tap.
  // Clarity showed LOAD MORE was the single most-clicked element on /collections/shop-now.
  // This watches the button and clicks it, reusing the existing click path.
  //
  // Selector is deliberately tag-agnostic: the button is wrapped in <link-to-button>, which
  // may swap the <a> for a <button> once it upgrades, so matching on 'a[...]' can find nothing.
  // Set up in connectedCallback with frame retries, because the theme editor constructs the
  // element before its children exist.
  connectedCallback() {
    if (!this.container) this.container = document.getElementById('ProductGrid');

    document.addEventListener('DOMContentLoaded', () => {
      const hash = window.location.hash;
      if (hash) {
        const el = document.querySelector(hash);
        const card = el && el.closest('.card-product');
        if (card) setTimeout(() => card.scrollIntoView(), 50);
      }
    });

    this.initAutoLoad();
  }

  disconnectedCallback() {
    this.stopAutoLoad();
  }

  loadMoreTrigger() {
    return this.querySelector('#ProductLoadMore [data-load-direction="more"]');
  }

  initAutoLoad(attempt) {
    if (this.autoLoadObserver) return;
    if (!('IntersectionObserver' in window)) return;

    const target = this.querySelector('#ProductLoadMore');
    if (!target) {
      if ((attempt || 0) < 10) {
        requestAnimationFrame(() => this.initAutoLoad((attempt || 0) + 1));
      }
      return;
    }

    this.autoLoadObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || this.isLoading) return;
          const trigger = this.loadMoreTrigger();
          if (trigger && trigger.getAttribute('aria-disabled') !== 'true') {
            trigger.click();
          }
        });
      },
      { rootMargin: '600px 0px' }
    );

    this.autoLoadObserver.observe(target);
  }

  stopAutoLoad() {
    if (this.autoLoadObserver) {
      this.autoLoadObserver.disconnect();
      this.autoLoadObserver = null;
    }
  }

  onClick(event) {
    if (this.isLoading) return;
    const trigger = event.target.closest ? event.target.closest('[data-load-id], [data-product-page]') : null;
    const source = trigger || event.target;

    const page = source.getAttribute('data-product-page');
    if (page) {
      // Update history so back button functionality works
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.set('page', page);
      history.replaceState(
        { urlParams: urlParams.toString(), page },
        '',
        `${window.location.href.split('?')[0]}?${urlParams.toString()}#${source.getAttribute('id')}`
      );
    }

    const id = source.getAttribute('data-load-id');
    if (id) {
      // Programmatic clicks on the underlying anchor would otherwise navigate to ?page=2.
      if (event.cancelable) event.preventDefault();
      this.setLoading();
      const direction = source.getAttribute('data-load-direction');
      const urlParams = new URLSearchParams(window.location.search);
      if (!this.page) {
        this.page = parseInt(urlParams.get('page')) || 1;
      }
      this.page = direction === 'more' ? this.page + 1 : this.page - 1;
      urlParams.set('page', this.page);
      const pageUrl = `${window.location.href.split('?')[0]}?${this.page > 1 ? urlParams.toString() : ''}`;
      const fetchUrl = `${pageUrl}&section_id=${id}`;
      history.replaceState({ urlParams: urlParams.toString() }, '', pageUrl);
      this.fetchData(fetchUrl, direction);
    }
  }

  setLoading() {
    const buttons = this.querySelectorAll('[data-load-id]');
    buttons.forEach((button) => {
      button.setAttribute('aria-disabled', true);
      button.setAttribute('data-loading', true);
    });
    this.isLoading = true;
  }

  fetchData(url, direction) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        this.renderData(responseText, direction);
      })
      .catch((err) => {
        if (err) {
          console.log(err);
        }
        // Reset the lock, otherwise one failed fetch freezes loading for the session.
        this.isLoading = false;
        document.dispatchEvent(new CustomEvent('rendering:error'));
      });
  }

  renderData(html, direction) {
    const parser = new DOMParser().parseFromString(html, 'text/html');
    const content = parser.getElementById('ProductGrid').innerHTML;
    let htmlPosition = 'beforeend';
    let paginationId = 'ProductLoadMore';
    if (direction === 'previous') {
      htmlPosition = 'afterbegin';
      paginationId = 'ProductLoadPrevious';
    }
    const paginationEl = parser.getElementById(paginationId);
    const holder = this.querySelector(`#${paginationId}`);
    if (holder) {
      if (paginationEl) {
        holder.innerHTML = paginationEl.innerHTML;
      } else {
        holder.style.display = 'none';
        // No further pages — stop observing so we don't spin on the last batch.
        if (direction === 'more') this.stopAutoLoad();
      }
    }
    this.container.insertAdjacentHTML(htmlPosition, content);
    this.isLoading = false;
    const buttons = this.querySelectorAll('[data-load-id]');
    buttons.forEach((button) => {
      button.removeAttribute('data-loading');
      button.removeAttribute('aria-disabled');
      const spinner = button.querySelector('.loading__spinner');
      if (spinner) spinner.classList.add('hidden');
    });
  }
}
customElements.define('collection-infinite-scroll', CollectionInfiniteScroll);
